import React, { lazy, Suspense, useLayoutEffect, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { MicGlyph } from './VoiceStatus';
import avatar from '../assets/keyan.png';

const HomeVoice = lazy(() => import('./HomeVoice'));

export default function HomeAvatar() {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef(null);
  const dialogRef = useRef(null);
  const transitionRef = useRef(null);
  const mountedRef = useRef(true);
  const requestRef = useRef(0);
  const wasOpenRef = useRef(false);

  const changeOpen = (next) => {
    const request = ++requestRef.current;
    transitionRef.current?.skipTransition();
    const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (document.startViewTransition && !reduceMotion) {
      const transition = document.startViewTransition(() => {
        if (mountedRef.current && request === requestRef.current) {
          flushSync(() => setOpen(next));
        }
      });
      transitionRef.current = transition;
      // Skipped transitions still apply the state update.
      transition.ready.catch(() => {});
    } else {
      setOpen(next);
    }
  };

  useLayoutEffect(() => {
    if (!open) {
      if (wasOpenRef.current) triggerRef.current?.focus({ preventScroll: true });
      wasOpenRef.current = false;
      return undefined;
    }
    wasOpenRef.current = true;
    const dialog = dialogRef.current;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    dialog.showModal();
    return () => {
      dialog.close();
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);

  useLayoutEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      transitionRef.current?.skipTransition();
    };
  }, []);

  return (
    <>
      <button
        type="button"
        className="personal-avatar-trigger"
        ref={triggerRef}
        aria-label="Open AI Keyan voice demo"
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => changeOpen(true)}
      >
        <img
          className="personal-portrait"
          src={avatar}
          width="112"
          height="112"
          alt="Illustration of Keyan Fayaz"
          style={{ viewTransitionName: open ? 'none' : 'profile-avatar' }}
        />
        <span className="avatar-entry-mic" aria-hidden="true"><MicGlyph size={14} /></span>
        <span className="avatar-entry-label" aria-hidden="true">Talk to AI Keyan</span>
      </button>
      <dialog
        className="home-voice-dialog"
        ref={dialogRef}
        aria-labelledby="home-voice-title"
        aria-describedby="home-voice-note"
        onCancel={(event) => { event.preventDefault(); changeOpen(false); }}
        onClose={() => { if (!dialogRef.current?.open) setOpen(false); }}
      >
        <div className="home-voice-heading">
          <h2 id="home-voice-title">AI Keyan</h2>
          <button type="button" className="home-voice-close" onClick={() => changeOpen(false)}>Close</button>
        </div>
        <p className="home-voice-intro">Talk to an AI version of me. It may get things wrong.</p>
        {open && (
          <VoiceLoadBoundary><Suspense fallback={
            <div className="home-voice-loading">
              <img src={avatar} alt="Illustration of Keyan Fayaz" style={{ viewTransitionName: 'profile-avatar' }} />
              <p role="status">Loading the demo...</p>
            </div>
          }>
            <HomeVoice />
          </Suspense></VoiceLoadBoundary>
        )}
        <p id="home-voice-note" className="home-voice-note">
          Starting uses your microphone and sends audio to ElevenLabs.
          Conversations last up to two minutes.
        </p>
      </dialog>
    </>
  );
}

class VoiceLoadBoundary extends React.Component {
  state = { failed: false };
  static getDerivedStateFromError() { return { failed: true }; }
  render() {
    return this.state.failed
      ? <p role="alert">The voice demo couldn't load. Reload the page to try again.</p>
      : this.props.children;
  }
}
